<template>
  <div class="combobox-editor" :style="{ width: `${cellWidth}px` }">
    <input
      ref="input"
      v-model="value"
      class="combobox-editor__input"
      :style="{ height: `${cellHeight}px` }"
      @input="onInput"
      @keydown="onKeydown"
    />
    <ul
      v-if="filteredOptions.length"
      ref="list"
      class="combobox-editor__list"
      :class="{ 'combobox-editor__list--up': opensUp }"
    >
      <li
        v-for="(option, index) in filteredOptions"
        :key="option"
        class="combobox-editor__option"
        :class="{
          'combobox-editor__option--active': index === activeIndex,
          'combobox-editor__option--selected': option === String(params.value),
        }"
        @mousedown.prevent="select(option)"
      >
        {{ option }}
      </li>
    </ul>
  </div>
</template>

<script>
import { isNil, toLower } from 'lodash';

// Bound into the styles below via v-bind()
const LIST_MAX_HEIGHT = 300;
const LIST_GAP = 4;

export default {
  data() {
    return {
      value: '',
      // Show the full list until the user types, then filter by their text
      isFiltering: false,
      activeIndex: -1,
      opensUp: false,
    };
  },

  computed: {
    options() {
      return (this.params.options || []).filter(o => !isNil(o) && o !== '').map(String);
    },

    filteredOptions() {
      if (!this.isFiltering) return this.options;
      const search = toLower(this.value);
      return this.options.filter(o => toLower(o).includes(search));
    },

    cellWidth() {
      return this.params.eGridCell.offsetWidth;
    },

    cellHeight() {
      return this.params.eGridCell.offsetHeight;
    },

    listMaxHeightPx() {
      return `${LIST_MAX_HEIGHT}px`;
    },

    listOffsetPx() {
      return `calc(100% + ${LIST_GAP}px)`;
    },
  },

  created() {
    const { eventKey, value } = this.params;
    // Excel-like: typing a character to start editing replaces the existing value
    if (eventKey?.length === 1) {
      this.value = eventKey;
      this.isFiltering = true;
    } else if (eventKey === 'Backspace' || eventKey === 'Delete') {
      this.value = '';
    } else {
      this.value = isNil(value) ? '' : String(value);
      this.activeIndex = this.options.indexOf(this.value);
    }
  },

  methods: {
    isPopup() {
      return true;
    },

    afterGuiAttached() {
      this.opensUp = this.shouldOpenUp();
      this.$refs.input.focus();
      this.scrollActiveIntoView();
    },

    shouldOpenUp() {
      const cell = this.params.eGridCell;
      const cellRect = cell.getBoundingClientRect();
      // The grid may clip popups, so use whichever bound is tighter
      const gridRect = cell.closest('.ag-root-wrapper')?.getBoundingClientRect();
      const top = Math.max(0, gridRect?.top ?? 0);
      const bottom = Math.min(window.innerHeight, gridRect?.bottom ?? window.innerHeight);
      const spaceBelow = bottom - cellRect.bottom;
      const spaceAbove = cellRect.top - top;
      return spaceBelow < LIST_MAX_HEIGHT + LIST_GAP && spaceAbove > spaceBelow;
    },

    getValue() {
      const trimmed = String(this.value ?? '').trim();
      return this.params.preventValueToLower ? trimmed : toLower(trimmed);
    },

    onInput() {
      this.isFiltering = true;
      this.activeIndex = -1;
    },

    onKeydown(event) {
      const last = this.filteredOptions.length - 1;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        // Stop the grid treating arrows as cell navigation
        event.stopPropagation();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = Math.min(last, Math.max(0, this.activeIndex + step));
        this.$nextTick(this.scrollActiveIntoView);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        // Let the event bubble so the grid ends editing and reads getValue()
        this.value = this.filteredOptions[this.activeIndex];
      }
    },

    select(option) {
      this.value = option;
      this.params.stopEditing();
    },

    scrollActiveIntoView() {
      this.$refs.list?.children[this.activeIndex]?.scrollIntoView({ block: 'nearest' });
    },
  },
};
</script>

<style lang="scss" scoped>
.combobox-editor {
  --combobox-padding-x: var(--ag-cell-horizontal-padding, 16px);

  position: relative;
  font-family: var(--ag-font-family);
  font-size: var(--ag-font-size);
  line-height: 1.5;

  * {
    box-sizing: border-box;
  }
}

.combobox-editor__input {
  width: 100%;
  // Subtract the border so text lines up with the list and grid cells
  padding: 0 calc(var(--combobox-padding-x) - 1px);
  font: inherit;
  outline: none;
  color: rgb(var(--v-theme-on-surface));
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 4px;
}

// Grows wider than the cell so options aren't truncated
.combobox-editor__list {
  position: absolute;
  top: v-bind(listOffsetPx);
  left: 0;
  min-width: 100%;
  width: max-content;
  max-width: 400px;
  max-height: v-bind(listMaxHeightPx);
  overflow-y: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &--up {
    top: auto;
    bottom: v-bind(listOffsetPx);
  }
}

.combobox-editor__option {
  padding: 6px var(--combobox-padding-x);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }

  &--selected {
    color: rgb(var(--v-theme-primary));
  }

  &--active,
  &--active:hover {
    background: rgba(var(--v-theme-on-surface), 0.1);
  }
}
</style>
